import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Add, Delete, Visibility, VisibilityOff } from "@mui/icons-material";
import { Alert, Badge, Button, Checkbox, FormControlLabel, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { ConfirmModal } from "@/components/ConfirmModal";
import { CrudModal } from "@/components/crud/CrudModal";
import { FilterModal } from "@/components/FilterModal";
import { TransferList } from "@/components/TransferList";
import { useFilterState } from "@/hooks/useFilterState";
import { DIA_SEMANA_LABEL, type Colaborador, type Escala, type Estrategico, type VinculoRegularAtivo, type VinculoTurma } from "@/api/escala.service";

// EscalaVinculoValues is salvar_vinculos' payload shape minus escala_id
// (that comes from which escala the modal was opened for, not from the
// form itself) — docs/escala/05-plano.md D2/D3/D4. Extraído de
// EscalaEscalasTab pra este arquivo próprio porque é aqui que essas regras
// (grupos estratégicos, dias cheio fixos, exclusividade REGULAR/
// COMPLEMENTAR — ver panels/escala_eletronica_unificada no repo do
// Vitruvio) vão crescer.
export interface EscalaVinculoValues {
  colaborador_ids: string[];
  estrategicos: Estrategico[];
  dias_cheios_fixos: number[];
  turmas: VinculoTurma[];
}

// validarVinculoValues espelha, no cliente, o que validateTurmas
// (internal/escala/vinculo.go, R30) exige no servidor: escala do tipo
// FOLGA_FIXA_SAB_ALT não aceita ninguém vinculado sem turma. O backend
// continua sendo a autoridade (a mesma checagem roda de novo lá); isto é só
// pra travar antes e nomear quem falta, em vez de um erro genérico depois
// do roundtrip.
export function validarVinculoValues(values: Partial<EscalaVinculoValues>, tipoEscala: string | undefined, colaboradores: Colaborador[]): string | null {
  if (tipoEscala !== "FOLGA_FIXA_SAB_ALT") return null;

  const comTurma = new Set((values.turmas ?? []).map((t) => t.colaborador_id));
  const nomes = new Map(colaboradores.map((c) => [c.id, c.nome]));
  const semTurma = (values.colaborador_ids ?? [])
    .filter((id) => !comTurma.has(id))
    .map((id) => nomes.get(id) ?? id);

  if (semTurma.length === 0) return null;
  return `Escala de sábado alternado exige turma pra todo mundo, falta em: ${semTurma.join("; ")}`;
}

// DIAS_CHEIOS_FIXOS_OPCOES são os dias válidos pra dia cheio fixo
// (05-plano.md D4: "segunda a sábado" — domingo tem distribuição própria no
// motor, appdono/CLAUDE.md passo 3), derivados do mesmo DIA_SEMANA_LABEL que
// EscalaEscalasTab usa pra dia_folga_fixa, só cortando o domingo (7).
const DIAS_CHEIOS_FIXOS_OPCOES = Object.entries(DIA_SEMANA_LABEL)
  .map(([dia, label]) => ({ dia: Number(dia), label }))
  .filter((o) => o.dia <= 6);

// MAX_GRUPOS_ESTRATEGICOS espelha a CHECK de escala_estrategico.grupo
// (1 a 5, docs/escala/02-modelo-de-dados.md) — MaxGruposEstrategicos em
// vinculo.go é a fonte da verdade, isso aqui é só pra desabilitar
// "Adicionar grupo" antes de bater no erro do backend.
const MAX_GRUPOS_ESTRATEGICOS = 5;

// MAX_COLABORADORES_POR_GRUPO_ESTRATEGICO espelha
// MaxColaboradoresPorGrupoEstrategico em vinculo.go (R36, limite do legado)
// — fonte da verdade é lá, isso aqui é só pra desabilitar o candidato antes
// de bater no erro do backend.
const MAX_COLABORADORES_POR_GRUPO_ESTRATEGICO = 3;

// MAX_DIAS_CHEIOS_FIXOS espelha MaxDiasCheiosFixos em vinculo.go (R36,
// limite do legado) — fonte da verdade é lá, isso aqui é só pra desabilitar
// os checkboxes restantes antes de bater no erro do backend.
const MAX_DIAS_CHEIOS_FIXOS = 2;

interface EscalaVinculoFormProps {
  values: Partial<EscalaVinculoValues>;
  setValues: Dispatch<SetStateAction<Partial<EscalaVinculoValues>>>;
  isEditing: boolean;
  colaboradores: Colaborador[];
  escalaId?: string;
  aplicaExclusividade: boolean;
  vinculosRegulares: VinculoRegularAtivo[];
  // R30: turma só é pedida/exigida quando a escala é FOLGA_FIXA_SAB_ALT — a
  // alternância de sábado do gerador depende dela; os demais tipos nem a
  // enviam.
  exigeTurma: boolean;
  // R26: grupos estratégicos e dias cheios fixos só têm efeito nos
  // geradores 6X1 e 5X2 — nos demais tipos as duas seções nem aparecem, pra
  // não deixar cadastrar configuração que o gerador daquele tipo nunca consulta.
  semEfeitoDeGrupos: boolean;
  // Cargas de uma escala com carga horária; null na escala sem. A geração ignora quem não tem uma
  // delas.
  cargasDaEscala: string[] | null;
}

// Filtro por nome/setor/cargo no mesmo FilterModal (components/
// FilterModal.tsx) que EscalaEscalasTab/EscalaColaboradoresTab já usam pras
// tabelas do módulo — um só botão "Filtros" com badge, Aplicar/Limpar, em
// vez do par de Autocomplete direto no corpo mais a busca embutida do
// TransferList que este modal tinha antes (dois filtros de nome
// concorrentes). `hideSearch` no TransferList some com a busca própria dele
// aqui — o campo "Nome" abaixo é quem filtra por nome agora. Estado do
// filtro fica aqui, não em EscalaVinculoValues — não é campo que se salva,
// e reseta sozinho a cada abertura porque o Modal do MUI desmonta o
// conteúdo ao fechar. Client-side: `colaboradores` já veio inteiro da loja
// (EscalaEscalasTab's handleVincular busca todos de uma vez), então
// filtrar nos dados já carregados evita um roundtrip a mais — só o "Aplicar"
// muda de imediato pra "ao clicar", igual as outras telas.
// FILTRO_VAZIO é o estado inicial/limpo do filtro nome+setor+cargo — usado
// tanto pelo filtro da equipe geral quanto por um por grupo estratégico
// (ver grupoInputValues/grupoFiltros), sempre com o mesmo FilterModal
// genérico (components/FilterModal.tsx) que o resto do sistema usa pra essa
// combinação de campos, nunca a busca embutida do TransferList.
// Filtro de texto livre (setor/cargo são texto do colaborador): contém, sem
// diferenciar maiúsculas; filtro vazio não exclui ninguém.
const contem = (valor: string | undefined, busca: string) =>
  !busca.trim() || (valor ?? "").toLowerCase().includes(busca.trim().toLowerCase());

const FILTRO_VAZIO = { nome: "", setor: "", cargo: "" };

const SEM_CARGA_DA_ESCALA = "Sem carga desta escala";

function EscalaVinculoForm({ values, setValues, colaboradores, escalaId, aplicaExclusividade, vinculosRegulares, exigeTurma, semEfeitoDeGrupos, cargasDaEscala }: EscalaVinculoFormProps) {
  const { inputValues, setInputValues, filtros, applyFilters, handleClearFilters } = useFilterState(FILTRO_VAZIO);

  // Nome de cada grupo estratégico vive fora de EscalaVinculoValues porque
  // escala_estrategico só tem uma linha por colaborador — um grupo sem
  // nenhum colaborador ainda não tem onde guardar o nome digitado.
  // Inicializado uma vez a partir do initialData (o modal desmonta ao
  // fechar, mesmo raciocínio de `filtros` acima), e reaplicado em toda
  // linha do grupo a cada edição — ver handleNomeGrupoChange.
  const [nomesGrupos, setNomesGrupos] = useState<Record<number, string>>(() => {
    const mapa: Record<number, string> = {};
    for (const e of values.estrategicos ?? []) {
      if (e.nome_grupo) mapa[e.grupo] = e.nome_grupo;
    }
    return mapa;
  });

  // gruposAtivos é a lista dinâmica de linhas renderizadas — não os cinco
  // valores fixos de 1 a MAX_GRUPOS_ESTRATEGICOS, só os grupos que o
  // usuário efetivamente criou nesta sessão do modal ("Adicionar
  // grupo"/"Remover grupo" abaixo). O número interno (`grupo`) é só a
  // chave que amarra os colaboradores de escala_estrategico; o que aparece
  // pro usuário é a posição na lista (ver `numeroExibido` no render).
  const [gruposAtivos, setGruposAtivos] = useState<number[]>(() => {
    const grupos = new Set<number>();
    for (const e of values.estrategicos ?? []) grupos.add(e.grupo);
    return Array.from(grupos).sort((a, b) => a - b);
  });

  // O twin column de cada grupo começa sempre escondido — inclusive um
  // grupo que já chega com colaborador (edição de uma escala já salva); só
  // abre com o ícone de olho ou ao criar o grupo agora mesmo (ver
  // handleAddGrupo), nunca sozinho ao abrir o modal — pedido explícito do
  // usuário, diferente do painel Vitruvio de origem, que auto-expandia
  // "Ver Restrições - Grupo N" quando a consulta inicial achava alguém.
  const [gruposExpandidos, setGruposExpandidos] = useState<Set<number>>(new Set());

  const handleAddGrupo = () => {
    setGruposAtivos((atual) => {
      if (atual.length >= MAX_GRUPOS_ESTRATEGICOS) return atual;
      let proximo = 1;
      while (atual.includes(proximo)) proximo++;
      // O grupo recém-criado abre expandido de cara — é pra isso que se
      // clicou "Adicionar grupo", não faria sentido precisar de um segundo
      // clique no ícone de olho pra começar a escolher quem entra nele.
      setGruposExpandidos((expandidos) => new Set(expandidos).add(proximo));
      return [...atual, proximo];
    });
  };

  // Ícone de lixeira pede confirmação antes de remover — mesmo ConfirmModal
  // (components/ConfirmModal.tsx) que MetasPeriodosTab usa pra excluir um
  // período, não um dialog próprio.
  const [grupoParaExcluir, setGrupoParaExcluir] = useState<number | null>(null);

  // Filtro nome+setor+cargo por grupo estratégico — cada linha tem o seu
  // (chaveado por `grupo`), mesma separação input-pendente/aplicado do
  // filtro da equipe geral acima, só que um mapa em vez de um state único
  // porque são até 5 twin columns independentes.
  const [grupoInputValues, setGrupoInputValues] = useState<Record<number, typeof FILTRO_VAZIO>>({});
  const [grupoFiltros, setGrupoFiltros] = useState<Record<number, typeof FILTRO_VAZIO>>({});

  const handleRemoveGrupo = (grupo: number) => {
    setGruposAtivos((atual) => atual.filter((g) => g !== grupo));
    setGruposExpandidos((atual) => {
      const proximo = new Set(atual);
      proximo.delete(grupo);
      return proximo;
    });
    setNomesGrupos((m) => {
      const { [grupo]: _removido, ...resto } = m;
      return resto;
    });
    setGrupoInputValues((m) => {
      const { [grupo]: _removido, ...resto } = m;
      return resto;
    });
    setGrupoFiltros((m) => {
      const { [grupo]: _removido, ...resto } = m;
      return resto;
    });
    setValues((v) => ({ ...v, estrategicos: (v.estrategicos ?? []).filter((e) => e.grupo !== grupo) }));
  };

  const handleToggleGrupoExpandido = (grupo: number) => {
    setGruposExpandidos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(grupo)) {
        proximo.delete(grupo);
      } else {
        proximo.add(grupo);
      }
      return proximo;
    });
  };

  const applyGrupoFilters = (grupo: number) => {
    setGrupoFiltros((m) => ({ ...m, [grupo]: grupoInputValues[grupo] ?? FILTRO_VAZIO }));
  };
  const handleClearGrupoFilters = (grupo: number) => {
    setGrupoInputValues((m) => ({ ...m, [grupo]: FILTRO_VAZIO }));
    setGrupoFiltros((m) => ({ ...m, [grupo]: FILTRO_VAZIO }));
  };

  // Quem não entra na geração (inativo, fora de escala ou sem carga desta escala) não é
  // oferecido em "Disponíveis"; se já estiver vinculado, continua na lista para poder sair.
  const motivoForaDaEscala = (c: Colaborador): string | undefined => {
    if (!c.ativo) return "Inativo";
    if (!c.entra_em_escala) return "Não entra em escala";
    if (cargasDaEscala && !cargasDaEscala.includes(c.carga_horaria_id ?? "")) return SEM_CARGA_DA_ESCALA;
    return undefined;
  };
  const vinculadosIds = values.colaborador_ids ?? [];
  const vinculadosForaDaEscala = colaboradores.filter(
    (c) => vinculadosIds.includes(c.id!) && motivoForaDaEscala(c),
  ).length;
  // Só os que ficaram de fora pela carga: inativo e fora de escala já não eram oferecidos.
  const semCargaForaDaLista = colaboradores.filter(
    (c) => !vinculadosIds.includes(c.id!) && motivoForaDaEscala(c) === SEM_CARGA_DA_ESCALA,
  ).length;

  const colaboradoresFiltrados = colaboradores.filter(
    (c) =>
      (vinculadosIds.includes(c.id!) || !motivoForaDaEscala(c)) &&
      c.nome.toLowerCase().includes(filtros.nome.trim().toLowerCase()) &&
      contem(c.setor, filtros.setor) &&
      contem(c.cargo, filtros.cargo),
  );

  const activeFilterCount = Object.values(filtros).filter((v) => v).length;

  const colaboradorEmGrupo = useMemo(() => {
    const mapa = new Map<string, number>();
    for (const e of values.estrategicos ?? []) mapa.set(e.colaborador_id, e.grupo);
    return mapa;
  }, [values.estrategicos]);

  const turmaPorColaborador = useMemo(() => {
    const mapa = new Map<string, "A" | "B">();
    for (const t of values.turmas ?? []) mapa.set(t.colaborador_id, t.turma);
    return mapa;
  }, [values.turmas]);

  const handleTurmaChange = (colaborador_id: string, turma: "A" | "B") => {
    setValues((v) => ({
      ...v,
      turmas: [...(v.turmas ?? []).filter((t) => t.colaborador_id !== colaborador_id), { colaborador_id, turma }],
    }));
  };

  // Traduz o `grupo` interno pra posição exibida ("Grupo 2") na hora de
  // avisar "já está em outro grupo" — o usuário nunca vê o número interno.
  const numeroExibidoPorGrupo = useMemo(() => {
    const mapa = new Map<number, number>();
    gruposAtivos.forEach((g, i) => mapa.set(g, i + 1));
    return mapa;
  }, [gruposAtivos]);

  // Um colaborador em no máximo um grupo por escala (05-plano.md D3): trocar
  // a seleção do grupo N remove esses colaboradores de qualquer outro grupo
  // em que estivessem antes.
  const handleGrupoSelecaoChange = (grupo: number, ids: string[]) => {
    setValues((v) => {
      const semEsseGrupoNemRealocados = (v.estrategicos ?? []).filter(
        (e) => e.grupo !== grupo && !ids.includes(e.colaborador_id),
      );
      const nomeGrupo = nomesGrupos[grupo] ?? "";
      // Mover vários de uma vez (selecionar tudo, "mover todos") não passa pelo
      // getDisabledReason de cada item: o teto do grupo é aplicado aqui.
      const doGrupo: Estrategico[] = ids
        .slice(0, MAX_COLABORADORES_POR_GRUPO_ESTRATEGICO)
        .map((colaborador_id) => ({ grupo, nome_grupo: nomeGrupo, colaborador_id }));
      return { ...v, estrategicos: [...semEsseGrupoNemRealocados, ...doGrupo] };
    });
  };

  const handleNomeGrupoChange = (grupo: number, nome: string) => {
    setNomesGrupos((m) => ({ ...m, [grupo]: nome }));
    setValues((v) => ({
      ...v,
      estrategicos: (v.estrategicos ?? []).map((e) => (e.grupo === grupo ? { ...e, nome_grupo: nome } : e)),
    }));
  };

  // Trava de exclusividade (docs/escala/01-dominio.md): "um colaborador tem
  // uma escala regular, e só uma". O backend já recusa isso em
  // salvar_vinculos — este mapa é só pra avisar/bloquear no TransferList
  // antes do submit, mesma regra, mesma exceção (o vínculo à própria escala
  // sendo editada não conta como conflito). TRANSFERENCIA não entra
  // (05-plano.md D2: "segue as próprias regras").
  const conflitoPorColaborador = useMemo(() => {
    const mapa = new Map<string, string>();
    if (!aplicaExclusividade) return mapa;
    for (const v of vinculosRegulares) {
      if (v.escala_id === escalaId) continue;
      mapa.set(v.colaborador_id, v.escala_nome);
    }
    return mapa;
  }, [aplicaExclusividade, vinculosRegulares, escalaId]);

  return (
    <>
      {aplicaExclusividade && conflitoPorColaborador.size > 0 && (
        <Alert severity="warning" sx={{ typography: "caption" }}>
          Colaboradores já vinculados a outra escala regular aparecem bloqueados abaixo.
        </Alert>
      )}
      {vinculadosForaDaEscala > 0 && (
        <Alert severity="warning" sx={{ typography: "caption" }}>
          {vinculadosForaDaEscala} colaborador(es) vinculado(s) estão inativos, fora de escala, sem carga
          desta escala ou em situação que não trabalha e não entram na geração. Remova quem não faz mais
          parte da equipe.
        </Alert>
      )}
      {semCargaForaDaLista > 0 && (
        <Alert severity="info" sx={{ typography: "caption" }}>
          {semCargaForaDaLista} colaborador(es) da loja não têm uma das cargas horárias desta escala e não
          aparecem para vincular.
        </Alert>
      )}
      {exigeTurma && (
        <Alert severity="info" sx={{ typography: "caption" }}>
          Escala de sábado alternado: a turma (A ou B) de cada colaborador é sugerida automaticamente
          ao vincular, balanceando os dois grupos. Troque ao lado do nome, na lista de vinculados, se
          quiser outra combinação antes de salvar.
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary">
        {(values.colaborador_ids ?? []).length} de {colaboradores.length} colaborador(es) da loja vinculado(s) à escala
      </Typography>
      <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
        <TextField
          label="Nome"
          size="small"
          fullWidth
          value={inputValues.nome}
          onChange={(e) => setInputValues((v) => ({ ...v, nome: e.target.value }))}
        />
        <TextField
          label="Setor"
          fullWidth
          value={inputValues.setor}
          onChange={(e) => setInputValues((v) => ({ ...v, setor: e.target.value }))}
          size="small"
        />
        <TextField
          label="Cargo"
          fullWidth
          value={inputValues.cargo}
          onChange={(e) => setInputValues((v) => ({ ...v, cargo: e.target.value }))}
          size="small"
        />
      </FilterModal>

      {/* Twin column genérico (components/TransferList.tsx, o mesmo que
          CompanyTransferList usa pra vincular empresa em UserForm), não
          Autocomplete: vincular equipe é tipicamente "a maioria da loja de
          uma vez", não escolher um colaborador por vez — mover
          tudo/filtrar/mover em lote é o caso comum aqui. */}
      <TransferList<Colaborador>
        items={colaboradoresFiltrados}
        selectedIds={values.colaborador_ids ?? []}
        onChange={(colaborador_ids) =>
          setValues((v) => {
            const turmasRestantes = (v.turmas ?? []).filter((t) => colaborador_ids.includes(t.colaborador_id));
            // Sugere turma pra quem entra agora (balanceando com quem já tem
            // turma), sem tocar em quem já estava vinculado — o clique nos
            // botões A/B abaixo ainda sobrescreve à vontade antes de salvar.
            const idsAnteriores = new Set(v.colaborador_ids ?? []);
            const recemAdicionados = exigeTurma ? colaborador_ids.filter((id) => !idsAnteriores.has(id)) : [];
            let emA = turmasRestantes.filter((t) => t.turma === "A").length;
            let emB = turmasRestantes.length - emA;
            const turmasSugeridas = recemAdicionados.map((colaborador_id) => {
              const turma: "A" | "B" = emA <= emB ? "A" : "B";
              if (turma === "A") emA++;
              else emB++;
              return { colaborador_id, turma };
            });
            return {
              ...v,
              colaborador_ids,
              // Só aceita estratégico quem já está vinculado (05-plano.md D3) —
              // tirar alguém da equipe também tira do grupo, sem exigir outro
              // clique. Mesma coisa pra turma (R30).
              estrategicos: (v.estrategicos ?? []).filter((e) => colaborador_ids.includes(e.colaborador_id)),
              turmas: [...turmasRestantes, ...turmasSugeridas],
            };
          })
        }
        getId={(c) => c.id!}
        getLabel={(c) => c.nome}
        getSecondaryLabel={(c) => {
          const motivo = motivoForaDaEscala(c);
          return motivo ? [c.matricula, motivo].filter(Boolean).join(" — ") : c.matricula || undefined;
        }}
        getDisabledReason={(c) => {
          const escalaNome = conflitoPorColaborador.get(c.id!);
          return escalaNome ? `Já vinculado à escala regular "${escalaNome}"` : undefined;
        }}
        renderRightExtra={
          exigeTurma
            ? (c) => (
                <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                  {(["A", "B"] as const).map((turma) => (
                    <Button
                      key={turma}
                      size="small"
                      variant={turmaPorColaborador.get(c.id!) === turma ? "contained" : "outlined"}
                      onClick={() => handleTurmaChange(c.id!, turma)}
                      sx={{ minWidth: 32, px: 1 }}
                    >
                      {turma}
                    </Button>
                  ))}
                </Stack>
              )
            : undefined
        }
        leftTitle="Disponíveis"
        rightTitle="Vinculados"
        itemNoun="colaborador(es)"
        hideSearch
      />

      {!semEfeitoDeGrupos && <>
      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        Grupos estratégicos
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
        Só colaboradores já vinculados à equipe podem entrar num grupo, cada um em no máximo um
        grupo, até {MAX_GRUPOS_ESTRATEGICOS} grupos por escala e até{" "}
        {MAX_COLABORADORES_POR_GRUPO_ESTRATEGICO} colaboradores por grupo.
      </Typography>
      <Stack spacing={1.5}>
        {gruposAtivos.map((grupo, index) => {
          const numeroExibido = index + 1;
          const idsDoGrupo = (values.estrategicos ?? [])
            .filter((e) => e.grupo === grupo)
            .map((e) => e.colaborador_id);
          const expandido = gruposExpandidos.has(grupo);
          const filtroGrupo = grupoFiltros[grupo] ?? FILTRO_VAZIO;
          const inputGrupo = grupoInputValues[grupo] ?? FILTRO_VAZIO;
          // Twin column só existe (abaixo) quando o grupo está expandido —
          // calcular esses dois filtros sobre a equipe inteira mesmo com o
          // grupo fechado era trabalho jogado fora a cada render do modal
          // (toda tecla digitada em qualquer filtro, de qualquer grupo).
          const colaboradoresGrupoFiltrados = expandido
            ? colaboradores
                .filter((c) => (values.colaborador_ids ?? []).includes(c.id!))
                .filter(
                  (c) =>
                    c.nome.toLowerCase().includes(filtroGrupo.nome.trim().toLowerCase()) &&
                    contem(c.setor, filtroGrupo.setor) &&
                    contem(c.cargo, filtroGrupo.cargo),
                )
            : [];
          const activeGrupoFilterCount = Object.values(filtroGrupo).filter((v) => v).length;

          return (
            <Stack key={grupo} spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label={`Nome do grupo ${numeroExibido}`}
                  size="small"
                  fullWidth
                  value={nomesGrupos[grupo] ?? ""}
                  onChange={(e) => handleNomeGrupoChange(grupo, e.target.value)}
                />
                <Tooltip title={expandido ? "Esconder colaboradores" : "Ver colaboradores"}>
                  <Badge badgeContent={idsDoGrupo.length} color="primary">
                    <IconButton
                      size="small"
                      aria-label={`${expandido ? "Esconder" : "Ver"} colaboradores do grupo ${numeroExibido}`}
                      onClick={() => handleToggleGrupoExpandido(grupo)}
                    >
                      {expandido ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </Badge>
                </Tooltip>
                <IconButton size="small" aria-label={`Remover grupo ${numeroExibido}`} onClick={() => setGrupoParaExcluir(grupo)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Stack>
              {/* Twin column só aparece com "Ver colaboradores" marcado —
                  até 5 grupos abertos ao mesmo tempo poluiria a tela, mesmo
                  toggle "Ver Restrições - Grupo N" do painel Vitruvio de
                  origem (panels/escala_eletronica_unificada). Filtro é o
                  mesmo FilterModal genérico do resto do sistema (nome +
                  setor + cargo), não a busca embutida do TransferList —
                  mesma padronização do filtro da equipe geral acima. */}
              {expandido && (
                <Stack spacing={1}>
                  <FilterModal
                    activeCount={activeGrupoFilterCount}
                    onApply={() => applyGrupoFilters(grupo)}
                    onClear={() => handleClearGrupoFilters(grupo)}
                  >
                    <TextField
                      label="Nome"
                      size="small"
                      fullWidth
                      value={inputGrupo.nome}
                      onChange={(e) => setGrupoInputValues((m) => ({ ...m, [grupo]: { ...inputGrupo, nome: e.target.value } }))}
                    />
                    <TextField
                      label="Setor"
                      fullWidth
                      value={inputGrupo.setor}
                      onChange={(e) => setGrupoInputValues((m) => ({ ...m, [grupo]: { ...inputGrupo, setor: e.target.value } }))}
                      size="small"
                    />
                    <TextField
                      label="Cargo"
                      fullWidth
                      value={inputGrupo.cargo}
                      onChange={(e) => setGrupoInputValues((m) => ({ ...m, [grupo]: { ...inputGrupo, cargo: e.target.value } }))}
                      size="small"
                    />
                  </FilterModal>
                  <TransferList<Colaborador>
                    items={colaboradoresGrupoFiltrados}
                    selectedIds={idsDoGrupo}
                    onChange={(ids) => handleGrupoSelecaoChange(grupo, ids)}
                    getId={(c) => c.id!}
                    getLabel={(c) => c.nome}
                    getSecondaryLabel={(c) => c.matricula || undefined}
                    getDisabledReason={(c) => {
                      const grupoAtual = colaboradorEmGrupo.get(c.id!);
                      if (grupoAtual !== undefined && grupoAtual !== grupo) {
                        return `Já está no grupo ${numeroExibidoPorGrupo.get(grupoAtual) ?? grupoAtual}`;
                      }
                      if (grupoAtual !== grupo && idsDoGrupo.length >= MAX_COLABORADORES_POR_GRUPO_ESTRATEGICO) {
                        return `Grupo já tem ${MAX_COLABORADORES_POR_GRUPO_ESTRATEGICO} colaboradores`;
                      }
                      return undefined;
                    }}
                    leftTitle="Disponíveis"
                    rightTitle="No grupo"
                    itemNoun="colaborador(es)"
                    hideSearch
                  />
                </Stack>
              )}
            </Stack>
          );
        })}
      </Stack>
      <Button
        size="small"
        startIcon={<Add />}
        onClick={handleAddGrupo}
        disabled={gruposAtivos.length >= MAX_GRUPOS_ESTRATEGICOS}
        sx={{ mt: 1, alignSelf: "flex-start" }}
      >
        Adicionar grupo
      </Button>

      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        Dias cheios fixos
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
        Ninguém folga nos dias marcados. Se existir um dia cheio único cadastrado em qualquer data
        de uma semana, os fixos daquela semana inteira são ignorados. No máximo{" "}
        {MAX_DIAS_CHEIOS_FIXOS} dias.
      </Typography>
      <Stack direction="row" flexWrap="wrap" columnGap={1}>
        {DIAS_CHEIOS_FIXOS_OPCOES.map(({ dia, label }) => {
          const diasAtuais = values.dias_cheios_fixos ?? [];
          const marcado = diasAtuais.includes(dia);
          const noLimite = !marcado && diasAtuais.length >= MAX_DIAS_CHEIOS_FIXOS;
          return (
            <FormControlLabel
              key={dia}
              label={label}
              slotProps={{ typography: { variant: "body2" } }}
              control={
                <Checkbox
                  size="small"
                  checked={marcado}
                  disabled={noLimite}
                  onChange={(e) =>
                    setValues((v) => {
                      const atual = v.dias_cheios_fixos ?? [];
                      return {
                        ...v,
                        dias_cheios_fixos: e.target.checked ? [...atual, dia] : atual.filter((d) => d !== dia),
                      };
                    })
                  }
                />
              }
            />
          );
        })}
      </Stack>
      </>}

      <ConfirmModal
        open={grupoParaExcluir !== null}
        title="Remover grupo"
        text={`Remover o grupo ${grupoParaExcluir !== null ? numeroExibidoPorGrupo.get(grupoParaExcluir) : ""}? Os colaboradores vinculados a ele perdem o vínculo com o grupo.`}
        btnText="Remover"
        onClose={() => setGrupoParaExcluir(null)}
        onConfirm={() => {
          if (grupoParaExcluir !== null) handleRemoveGrupo(grupoParaExcluir);
          setGrupoParaExcluir(null);
        }}
      />
    </>
  );
}

interface EscalaVinculoModalProps {
  escala: Escala | null;
  initialData: EscalaVinculoValues | null;
  colaboradores: Colaborador[];
  vinculosRegulares: VinculoRegularAtivo[];
  onClose: () => void;
  onSubmit: (values: Partial<EscalaVinculoValues>) => void | Promise<void>;
}

// A busca de detalhe/colaboradores e o salvamento (log, notify) continuam no
// tab que abre este modal — é ele quem já orquestra o CRUD de Escala e
// segue o mesmo padrão pras outras ações da tela. Este arquivo é só a UI e
// as regras de vínculo em si. vinculosRegulares vem pronto do tab (escala.listar_vinculos_regulares).
export function EscalaVinculoModal({ escala, initialData, colaboradores, vinculosRegulares, onClose, onSubmit }: EscalaVinculoModalProps) {
  // Exclusividade só vale para REGULAR/COMPLEMENTAR — TRANSFERENCIA segue
  // as próprias regras (mesma condição de vinculo.go's
  // NewSalvarVinculosMethod, docs/escala/05-plano.md D2).
  const aplicaExclusividade = escala?.caracteristica === "REGULAR" || escala?.caracteristica === "COMPLEMENTAR";
  // R30: mesma condição de TipoFolgaFixaSabAlt em vinculo.go.
  const exigeTurma = escala?.tipo === "FOLGA_FIXA_SAB_ALT";
  // Mesmos tipos de disputaFolga (motor/viabilidade.go).
  const semEfeitoDeGrupos = !!escala?.tipo && escala.tipo !== "6X1" && escala.tipo !== "5X2";

  // Fora do 6X1 e do 5X2 a seção nem aparece (R26), mas se a escala já teve grupos/dias cheios
  // cadastrados de quando era um deles (tipo é editável antes de gerar), `initialData` ainda os
  // carrega escondidos em `values` — sem isto eles eram resubmetidos a cada save por causa de
  // um campo qualquer sem relação nenhuma. Some daqui, não só da tela.
  const handleSubmit = (values: Partial<EscalaVinculoValues>) =>
    onSubmit(semEfeitoDeGrupos ? { ...values, estrategicos: [], dias_cheios_fixos: [] } : values);

  return (
    <CrudModal<
      EscalaVinculoValues,
      {
        colaboradores: Colaborador[];
        escalaId?: string;
        aplicaExclusividade: boolean;
        vinculosRegulares: VinculoRegularAtivo[];
        exigeTurma: boolean;
        semEfeitoDeGrupos: boolean;
        cargasDaEscala: string[] | null;
      }
    >
      open={!!escala}
      onClose={onClose}
      title={`Equipe de ${escala?.nome ?? ""}`}
      initialData={initialData}
      onSubmit={handleSubmit}
      Form={EscalaVinculoForm}
      extraFormProps={{
        colaboradores,
        escalaId: escala?.id,
        aplicaExclusividade,
        vinculosRegulares,
        exigeTurma,
        semEfeitoDeGrupos,
        cargasDaEscala: escala?.com_carga_horaria ? escala.cargas_horarias_ids ?? [] : null,
      }}
    />
  );
}
